import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Star, ExternalLink, Quote } from 'lucide-react';

const reviews = [
  {
    id: 1,
    name: "Sarah M.",
    location: "Buchanan, MI",
    rating: 5,
    date: "2 weeks ago",
    text: "Absolutely transformed my home! The team understood exactly what I needed for my ADHD-friendly systems. My kitchen actually stays organized now, and I can find everything easily. Worth every penny!",
    service: "ADHD-Friendly Organization"
  },
  {
    id: 2,
    name: "Jennifer K.",
    location: "Niles, MI", 
    rating: 5,
    date: "1 month ago",
    text: "They helped us stage our house and we got 3 offers within a week! The before and after photos speak for themselves. Professional, efficient, and they really know the Michigan market.",
    service: "Real Estate Staging"
  },
  {
    id: 3,
    name: "Michael R.",
    location: "Berrien Springs, MI",
    rating: 5,
    date: "3 weeks ago",
    text: "Best investment we've made for our home. The seasonal organization system they set up makes Michigan's crazy weather changes so much easier to handle. No more hunting for winter coats in July!",
    service: "Seasonal Organization"
  },
  {
    id: 4,
    name: "Lisa T.",
    location: "St. Joseph, MI",
    rating: 5,
    date: "2 months ago", 
    text: "I was drowning in clutter after my move. The team was patient, non-judgmental, and created systems that actually work for my lifestyle. My stress levels have dropped significantly!",
    service: "Home Reset"
  },
  {
    id: 5,
    name: "David P.",
    location: "Benton Harbor, MI",
    rating: 5,
    date: "1 month ago",
    text: "Professional, punctual, and incredibly thorough. They organized our entire basement and garage in one day. Now we can actually park in our garage again! Highly recommend.",
    service: "Same-Day Service"
  },
  {
    id: 6,
    name: "Amy L.",
    location: "New Buffalo, MI",
    rating: 5,
    date: "3 weeks ago",
    text: "The consultation alone was worth it - they gave us so many actionable tips. The full service exceeded our expectations. Our home finally feels peaceful and functional.",
    service: "Home Organization"
  }
];

const GoogleReviewsSection = () => {
  const averageRating = 5.0;
  const totalReviews = 47;

  const handleViewAllReviews = () => {
    window.open('https://www.google.com/search?q=Organize+by+Design+Michigan+reviews', '_blank');
  };

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="https://upload.wikimedia.org/wikipedia/commons/c/c1/Google_%22G%22_logo.svg" 
              alt="Google"
              className="w-8 h-8 mr-3"
            />
            <div className="flex items-center space-x-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-6 h-6 fill-yellow-400 text-yellow-400" />
                ))}
              </div>
              <span className="text-2xl font-bold text-foreground">{averageRating}</span>
              <span className="text-muted-foreground">({totalReviews} reviews)</span>
            </div>
          </div>
          <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4">
            What Michigan Families Say About Us
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Real reviews from real Michigan homeowners who've transformed their spaces with our help.
          </p>
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {reviews.map((review) => (
            <Card key={review.id} className="hover-lift">
              <CardContent className="p-6">
                {/* Quote Icon */}
                <div className="mb-4">
                  <Quote className="w-8 h-8 text-accent/40" />
                </div>

                {/* Review Text */}
                <p className="text-muted-foreground mb-4 leading-relaxed">
                  "{review.text}"
                </p>

                {/* Rating */}
                <div className="flex items-center mb-3">
                  {[...Array(review.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>

                {/* Reviewer Info */}
                <div className="border-t pt-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-semibold text-foreground text-sm">
                        {review.name}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {review.location}
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {review.date}
                    </p>
                  </div>
                  <div className="text-xs text-accent font-medium">
                    {review.service}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* View All Reviews CTA */}
        <div className="text-center">
          <Button 
            onClick={handleViewAllReviews}
            variant="outline"
            size="lg"
            className="group"
          >
            View All {totalReviews} Google Reviews
            <ExternalLink className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Button>
          
          <div className="mt-6 p-6 bg-secondary/10 rounded-lg max-w-2xl mx-auto">
            <h3 className="font-semibold text-foreground mb-2">
              Love Your Organized Space?
            </h3>
            <p className="text-sm text-muted-foreground mb-4">
              Help other Michigan families discover the peace of an organized home by sharing your experience on Google.
            </p>
            <Button 
              onClick={() => window.open('https://g.page/r/organize-by-design-michigan/review', '_blank')}
              size="sm"
              className="luxury-gradient hover:opacity-90 text-white font-semibold"
            >
              Leave a Google Review
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GoogleReviewsSection;